import { Button } from "@/components/ui/button";
import { useState } from "react";

/**
 * Customer-facing approval page for staff impersonation (WARD-47). Reached by
 * clicking the emailed link. Approval is an explicit, conscious click — we
 * never auto-approve on load. On success the BFF has set the supervised
 * session cookie, so we send the customer to their dashboard, where the
 * non-dismissible banner makes the active session unmistakable.
 */
type State = "idle" | "working" | "error";

export function ApproveImpersonationRoute() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string>("");

  async function approve() {
    setState("working");
    setError("");
    try {
      const res = await fetch("/bff/impersonation/approve", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(reason(body.error));
        setState("error");
        return;
      }
      window.location.assign("/");
    } catch {
      setError("Something went wrong. Please try again.");
      setState("error");
    }
  }

  return (
    <Centered>
      <Eyebrow>support access</Eyebrow>
      <h1 className="mt-4 font-sans text-2xl font-medium tracking-tight text-foreground">
        Approve Wardkey support access
      </h1>
      {!token && (
        <p className="mt-4 text-[14px] text-muted-foreground">
          This link is missing its token. Please use the button in the email we sent you.
        </p>
      )}
      {token && (
        <>
          <p className="mt-4 max-w-[34rem] text-[14px] leading-relaxed text-muted-foreground">
            Approving lets a Wardkey staff member act on your behalf for up to{" "}
            <strong className="text-foreground">30 minutes</strong> to help with your request.
            You'll see a banner the whole time showing exactly who is helping, and access ends
            automatically. You can ignore this page to deny.
          </p>
          {error && (
            <p className="mt-4 rounded-md border border-[#FFB85C]/30 bg-[#FFB85C]/10 px-3 py-2 text-[13px] text-[#FFB85C]">
              {error}
            </p>
          )}
          <Button type="button" onClick={approve} disabled={state === "working"} className="mt-6">
            {state === "working" ? "Approving…" : "Approve for 30 minutes"}
          </Button>
        </>
      )}
    </Centered>
  );
}

function reason(code: string | undefined): string {
  switch (code) {
    case "expired":
      return "This approval link has expired. Ask support to send a new one.";
    case "already_consumed":
      return "This link has already been used.";
    default:
      return "This approval link is invalid.";
  }
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-[520px] rounded-2xl border border-border bg-card p-8 fade-up">
        {children}
      </div>
    </div>
  );
}

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
      {children}
    </span>
  );
}
